/**
 * The balance, rendered as the trading balance and the three parts it is made
 * of — and, where the venue has not sent those, as four components and a total
 * (invariant 9).
 *
 * **It adds up to the header** (`walletView`). The header shows the wallet's
 * trading balance; this panel used to headline `equity`, which is smaller by
 * every trade whose market has not settled on chain, and a trader with a busy
 * hour saw $136k in one place and $37k in the other with nothing between them.
 * Now the panel starts from the same number and writes it out as a sum — what
 * can be traded, plus what resting orders hold, plus what is waiting for a
 * market to settle (which is also where shares since sold are, because a
 * trade's money moves only at its market's one settlement), equals the total.
 * Money on its way in is one line outside the sum, shown only when there is
 * some. `equity` stays on the wire and off this screen.
 *
 * What follows is the four-component layout, still used against a venue that
 * sends no wallet terms (the frozen mock, or before the wallet is observed).
 *
 * **The components are never conflated and never summed into one number on
 * their own.** `equity` is shown because the venue publishes it; the four are
 * shown because a single figure cannot answer "where did my money go", which on
 * a venue with a settlement boundary every sixty seconds is the question a user
 * asks most.
 *
 * **`order_locked` says *Reserved*** (§F26l). It is the one component with no
 * on-chain source and it is not a lock: orders rest off-chain, nothing holds
 * that USDC, it sits in the user's own wallet, and if they move it the fill
 * fails. Calling it *locked* on screen would assert something the system does
 * not do.
 *
 * **The identity is checked here and shown when it fails.** `balanceView`
 * re-adds the four in `bigint` micro-USDC and compares against the published
 * `equity`; a disagreement renders as a warning row carrying both numbers. The
 * alternative — pick whichever reads better — is how a reconciliation bug ships.
 *
 * The three forecasts sit below a rule, visually outside the identity, because
 * `position_value` and `unrealized_pnl` are marks and a mark is an opinion:
 * putting one inside equity makes equity move when nothing happened.
 *
 * Subscribed here, not by the screen (invariant 2): `useBalance` is
 * `useSyncExternalStore`, and hoisting it into `<PortfolioRoute>` would
 * re-render the positions table on every balance frame.
 */

import { memo } from 'react'
import { NumCell, Skeleton } from '@/components/primitives'
import { balanceView, formatMicro, walletView, type WalletView } from '@/domain/balance'
import { useBalance } from '@/app/use-runtime'
import { formatSignedUsdc, formatUsdc } from '@/lib/format'
import { parseFixed, USDC_DP } from '@/lib/decimal'
import { cx } from '@/lib/cx'

/**
 * The three marks, and they are *absent*, never zero. A venue that has published
 * no mark for an account holding nothing has not forecast `$0.00` — it has
 * forecast nothing, and rendering the former states something it never said.
 */
const FORECASTS = [
  { label: 'Position value', field: 'position_value', signed: false },
  { label: 'Unrealized', field: 'unrealized_pnl', signed: true },
  { label: 'If all settle', field: 'pending_settlement_max_payout', signed: false },
] as const

export interface BalancePanelProps {
  className?: string
}

export const BalancePanel = memo(function BalancePanel({ className }: BalancePanelProps) {
  const balance = useBalance<Record<string, unknown>>()
  const view = balanceView(balance)

  if (!balance) return <Skeleton className={cx('h-[calc(136*var(--ud-u))] w-full', className)} />

  const wallet = walletView(balance)
  if (wallet) return <WalletBalance view={wallet} className={className} />

  // Absent, not zero. The venue publishes no mark for an account holding
  // nothing, and rendering `$0.00` for it would state a forecast it never made.
  const forecasts: { label: string; signed: boolean; value: string }[] = []
  for (const f of FORECASTS) {
    const value = stringOf(balance, f.field)
    if (value !== null) forecasts.push({ label: f.label, signed: f.signed, value })
  }

  return (
    <div className={cx('flex flex-col gap-3 px-3 py-3', className)} data-testid="balance-panel">
      <div className="flex items-baseline gap-2">
        <span className="text-caption uppercase text-text-tertiary">Equity</span>
        <NumCell
          value={view.equity === null ? '—' : formatUsdc(view.equity)}
          {...(view.equityMicro !== null ? { compare: view.equityMicro } : {})}
          className="text-title text-text-primary"
          reserve={12}
        />
      </div>

      {/*
        Wrapped, not stretched. A four-column grid across a 1600px viewport puts
        `Settling` a metre from `Available` and breaks the one thing the
        decomposition is for — being read as a single sum, left to right.
      */}
      <dl className="flex flex-wrap gap-x-8 gap-y-2">
        {view.rows.map((row) => (
          <div key={row.key} className="flex flex-col gap-0_5">
            <dt className="text-caption text-text-tertiary">{row.label}</dt>
            <dd>
              <NumCell
                value={formatUsdc(row.value)}
                compare={row.micro}
                align="left"
                className="text-body text-text-secondary"
                reserve={10}
              />
            </dd>
          </div>
        ))}
      </dl>

      {/*
        Only ever rendered when the venue disagrees with itself. `reconciles`
        is `null` — not `false` — while `equity` has not arrived, so a partial
        payload does not raise an alarm about arithmetic that has not happened.
      */}
      {view.reconciles === false ? (
        <p role="status" className="rounded-sm bg-danger-bg px-2 py-1_5 text-caption text-danger-text">
          These four add up to {formatMicro(view.componentSumMicro)}, and the venue reports{' '}
          {view.equity}. One of the two is wrong; nothing here is guessing which.
        </p>
      ) : null}

      {/*
        The rule and the forecasts appear together or not at all. A venue with
        nothing marked to market publishes none of the three, and a horizontal
        rule with nothing under it reads as a panel that failed to load.
      */}
      {forecasts.length > 0 ? (
        <div className="flex flex-wrap gap-x-6 gap-y-1_5 border-t border-border-subtle pt-2">
          {forecasts.map((f) => (
            <Forecast key={f.label} label={f.label} value={f.value} signed={f.signed} />
          ))}
        </div>
      ) : null}

      <p className="max-w-[68ch] text-caption text-text-tertiary">
        Reserved is held against your resting orders — the money stays in your own
        wallet, so withdrawing it means those orders may not fill.
        {forecasts.length > 0
          ? ' Position value and unrealized are marks, not money: they are outside the total above.'
          : ''}
      </p>
    </div>
  )
})

// ---------------------------------------------------------------------------

/**
 * The trading balance, written out as a sum that reads like a receipt:
 *
 *       Available to trade
 *     + Held for your open orders
 *     + In trades waiting to settle
 *     ───────────────────────────────
 *     = Trading balance
 *
 * The three lines add up to the total exactly (`walletView` checks it, and the
 * warning below says so when they do not). The `+` and `=` are on the screen
 * because "these add up to that" was the one thing the earlier row of six
 * figures could not say: four of them were outside the sum and nothing showed
 * which.
 *
 * Money on its way in is one sentence under the sum, and only when there is
 * some — a forecast the chain has not delivered, which a reader must not add.
 * What shares are worth, and what they cost, are the Positions table's (per
 * market: size, average price, mark, value, P&L); they describe positions, not
 * this balance.
 */
function WalletBalance({ view, className }: { view: WalletView; className?: string | undefined }) {
  const incoming = view.incoming !== null && view.incoming.micro > 0n ? view.incoming : null

  return (
    <div className={cx('flex flex-col gap-3 px-3 py-3', className)} data-testid="balance-panel">
      <div className="flex items-baseline gap-2" data-testid="balance-total">
        <span className="text-caption uppercase text-text-tertiary">Trading balance</span>
        <NumCell
          value={formatUsdc(view.total.value)}
          compare={view.total.micro}
          className="text-title text-text-primary"
          reserve={12}
        />
      </div>

      {/*
        Capped, not stretched: a label and its amount a metre apart across a
        wide screen stop reading as one line of a sum.
      */}
      <dl className="flex w-full max-w-[calc(480*var(--ud-u))] flex-col gap-1_5" data-testid="balance-parts">
        {view.rows.map((row, i) => (
          <SumLine key={row.key} part={row.key} sign={i === 0 ? null : '+'} label={row.label}
            value={row.value} micro={row.micro}
            note={row.key === 'unsettled' && row.micro > 0n
              ? `${formatUsdc(formatMicro(view.waiting.trading))} in markets still trading · ${formatUsdc(formatMicro(view.waiting.ended))} in markets that have ended`
              : null} />
        ))}
        <SumLine part="total" sign="=" label="Trading balance" value={view.total.value} micro={view.total.micro}
          note={null} total />
      </dl>

      {!view.reconciles ? (
        <p role="status" className="rounded-sm bg-danger-bg px-2 py-1_5 text-caption text-danger-text">
          {view.overReserved
            ? `Your resting orders and unsettled trades add up to more than your balance, because money left it while orders still held it. Some of those orders may not fill.`
            : `These three add up to ${formatMicro(view.sumMicro)}, and your balance is ${view.total.value}. One of the two is wrong; nothing here is guessing which.`}
        </p>
      ) : null}

      {incoming ? (
        <p className="max-w-[68ch] text-body-sm text-text-secondary" data-testid="balance-incoming">
          On its way:{' '}
          <NumCell value={formatSignedUsdc(incoming.value)} compare={incoming.micro} align="left"
            className="text-text-primary" />{' '}
          from winnings and sales, added when each market pays out — usually seconds after it ends.
        </p>
      ) : null}

      <p className="max-w-[68ch] text-caption text-text-tertiary">
        Your balance stays in your own wallet. A trade&apos;s cost waits there until its market pays
        out, so it is not available to trade; money held for your open orders is still yours.
      </p>
    </div>
  )
}

/**
 * One line of the sum: the operator, the label (with an optional note under
 * it), and the amount, right-aligned so the column adds up by eye.
 */
function SumLine({ part, sign, label, value, micro, note, total = false }: {
  part: string
  sign: '+' | '=' | null
  label: string
  value: string
  micro: bigint
  note: string | null
  total?: boolean
}) {
  return (
    <div
      className={cx('flex items-baseline justify-between gap-6', total && 'mt-0_5 border-t border-border-default pt-2')}
      data-part={part}
    >
      <dt className="flex min-w-0 items-baseline gap-2">
        {/* Visual only: the labels read as a list without it, and "plus" spoken before each would not help. */}
        <span aria-hidden="true" className="inline-block w-3 shrink-0 text-center text-text-tertiary">
          {sign ?? ''}
        </span>
        <span className="flex min-w-0 flex-col gap-0_5">
          <span className={total ? 'text-body text-text-primary' : 'text-body-sm text-text-secondary'}>{label}</span>
          {note ? <span className="text-caption text-text-tertiary">{note}</span> : null}
        </span>
      </dt>
      <dd>
        <NumCell
          value={formatUsdc(value)}
          compare={micro}
          className={total ? 'text-body font-medium text-text-primary' : 'text-body text-text-secondary'}
          reserve={12}
        />
      </dd>
    </div>
  )
}


/**
 * A forecast. Deliberately quieter than a component and on the other side of a
 * rule, because the one thing a reader must not do is add it to the four above.
 */
function Forecast({ label, value, signed = false }: { label: string; value: string; signed?: boolean }) {
  const negative = signed && value.trimStart().startsWith('-')
  return (
    <div className="flex items-baseline gap-1_5">
      <span className="text-caption text-text-tertiary">{label}</span>
      <NumCell
        value={signed ? formatSignedUsdc(value) : formatUsdc(value)}
        {...compareOf(value)}
        align="left"
        className={cx('text-body-sm', negative ? 'text-down-text' : 'text-text-secondary')}
      />
    </div>
  )
}

function stringOf(payload: unknown, field: string): string | null {
  const value = (payload as Record<string, unknown> | null)?.[field]
  return typeof value === 'string' ? value : null
}

/** No `compare` at all rather than a float one, if the string will not parse. */
function compareOf(value: string): { compare?: bigint } {
  try {
    return { compare: parseFixed(value, USDC_DP) }
  } catch {
    return {}
  }
}
